/**
 * exchange_noti_page_v2.js — DAS-106 Phase 04: wallet.jsx WalletDesktopV1/MobileV1 리디자인
 *
 * 원본 시안: wallet.jsx::WalletDesktopV1 + WalletMobileV1
 * UI: IS 리프레시 — TopBar breadcrumb + 4-KPI inline row + 카드 스택
 *
 * ── 시안 ↔ 운영 도메인 매핑 전략 ──────────────────────────────
 *   시안은 거래소 입출금 상태(지갑) 데이터를 직접 사용하고 있어
 *   우리 exchange_noti 도메인(공지+지갑 상태)과 거의 동일한 의미 구조.
 *   아래와 같이 1:1 매핑:
 *     시안 "전체 코인"       → walletStats.total_coins
 *     시안 "정상 비율"       → walletStats.normal_ratio
 *     시안 "정지 코인"       → walletStats.suspended_count (DAS-40 클릭 필터 유지)
 *     시안 "24시간 변경"     → walletStats.recent_changes_24h
 *     시안 신규 상장 공지   → NewListingNoticesPanel (거래소 신규 상장 공지)
 *     시안 거래지원 종료 공지 → DelistingNoticesPanel (거래지원 종료 공지)
 *     시안 지갑 입출금 상태 테이블 → WalletStatusTable (입출금 상태 실데이터)
 *     시안 상태 변경 이력   → WalletChangeLog (변경 이력 실데이터)
 *
 * ── 시안 V1 시각 디테일 (wallet.jsx::WalletDesktopV1) ─────────
 *   .app-body gap          → 20 (V1 기준)
 *   페이지 헤더            → breadcrumb "거래소 / 지갑 상태" + title "Wallet Status"
 *   4-KPI row              → .row gap:14, 4-col (모바일 2x2)
 *   정지 카드              → outline border-loss + 클릭 진입 (DAS-40)
 *   KPI value              → JetBrains Mono 30/600 -0.02em
 *   카드 섹션              → border-radius:16, bg-surface-card, border-surface-border, padding:18
 *   섹션 헤더(.c-head)     → flex baseline justify-between, title 13/600 -0.01em
 *
 * ── V1 컴포넌트 재사용 (기능 회귀 0 보장) ──────────────────────
 *   WalletStatusTable, WalletChangeLog, NewListingNoticesPanel,
 *   DelistingNoticesPanel, ServiceConnectionIndicator
 *
 * state: URL 쿼리 동기화 (status/listed/market/curr/range/kind) 포함
 * mock 데이터 미사용 — 모든 수치는 실데이터 API
 * TODO(DAS-106): wallet 시안 "8 거래소 기준" sub — 현재 upbit 단일, 다중 거래소 확장 예정
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
// V1 컴포넌트 재사용 (기능 회귀 0 보장)
import WalletStatusTable from '../exchange_noti_page/wallet_status_table';
import WalletChangeLog from '../exchange_noti_page/wallet_change_log';
import DelistingNoticesPanel from '../exchange_noti_page/delisting_notices_panel';
import NewListingNoticesPanel from '../exchange_noti_page/new_listing_notices_panel';
import ServiceConnectionIndicator from '../exchange_noti_page/service_connection_indicator';
import {
    fetchWalletStatus,
    fetchWalletStatusStats,
    fetchWalletStatusChanges,
    fetchNotiHealth,
} from '../../services/exchangeNotiApi';
import { getAccessToken, getStoredUsername, requireLoginOrThrow, clearLoginSession } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import DashboardWebSocket from '../../services/websocketClient';
import WalletChangeWebSocket from '../../services/walletWebSocketClient';
import { withRouter } from '../../utils/withRouter';
import { showToast } from '../../utils/show_toast';
import { StatusDot, KpiLabel } from '../common/claude_primitives';

/* ── KPI 셀 (시안 V1 4-KPI row — padding:18, KpiValue 30, mono) ──────────
 *   tone='loss' + outline=true: 정지 카드 → border-loss 강조 (시안 정합)
 *   onClick: button 시맨틱 + 키보드 진입 (DAS-40 — 정지 카드 필터 toggle)
 *   모바일: fontSize 22, padding 14
 */
function KpiCell({ label, value, tone, sub, isMobile, onClick, active, outline }) {
    const isClickable = typeof onClick === 'function';
    const valueSize = isMobile ? 22 : 30;
    const valueColor = tone ? `var(--color-${tone})` : 'var(--color-content)';

    const baseStyle = {
        padding: isMobile ? 14 : 18,
        borderRadius: 16,
        background: 'var(--color-surface-card)',
        border: outline || active
            ? '1px solid var(--color-loss)'
            : '1px solid var(--color-surface-border)',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'border-color .15s, background .15s',
        minWidth: 0,
    };

    const handleKey = (e) => {
        if (!isClickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? onClick : undefined}
            onKeyDown={isClickable ? handleKey : undefined}
            aria-pressed={isClickable ? !!active : undefined}
            style={baseStyle}
        >
            <KpiLabel>{label}</KpiLabel>
            <div style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: valueSize,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: valueColor,
                lineHeight: 1.05,
            }}>
                {value}
            </div>
            {sub && (
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 11,
                    color: 'var(--color-content-muted)',
                    marginTop: 6,
                }}>
                    {sub}
                </div>
            )}
        </div>
    );
}

/* ── 섹션 헤더 (.c-head — 시안 V1 정합) ──────────────────────────────────
 *   flex baseline justify-between, title 13/600 -0.01em, mb:12
 */
function SectionHead({ title, right }) {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
        }}>
            {/* 섹션 제목은 heading 이어야 한다 — div 로 두면 스크린리더에 페이지 구조가 없다 (DAS-176) */}
            <h2 style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--color-content)',
            }}>
                {title}
            </h2>
            {right && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {right}
                </div>
            )}
        </div>
    );
}

/* ── ExchangeNotiPageV2 메인 클래스 컴포넌트 ──────────────────────────── */
class ExchangeNotiPageV2 extends React.Component {
    constructor(props) {
        super(props);
        const sp = props.searchParams || new URLSearchParams();
        this.state = {
            collapsed: getNavCollapsed(),
            isMobile: isMobileViewport(),
            // 지갑 상태
            walletStatus: [],
            walletStatusLoading: true,
            walletStatusError: false,
            walletStatusLastUpdated: null,
            walletSearchQuery: '',
            // URL 쿼리 복원 (V1 DEFAULTS 와 완전 일치)
            walletStatusFilter: sp.get('status') || 'suspended',
            walletListedOnly: sp.get('listed') !== 'false',
            walletMarketFilter: sp.get('market') || null,
            // 통계
            walletStats: null,
            walletStatsLoading: true,
            walletStatsError: false,
            // 변경 이력
            walletChanges: [],
            walletChangesLoading: true,
            walletChangesError: false,
            walletChangesPagination: { count: 0, next: null, previous: null },
            walletChangesOffset: 0,
            walletChangesFilterCurrency: sp.get('curr') || '',
            walletChangesRange: sp.get('range') || '7d',
            walletChangesKind: sp.get('kind') || 'all',
            // WS + 서비스 헬스
            wsStatus: 'disconnected',
            isPollingFallback: false,
            serviceHealthStatus: 'loading',
            // 실시간 하이라이트
            walletHighlightIds: [],
        };
        this.dashboardWs = null;
        this.walletPollingInterval = null;
        this._resizeListener = null;
        this.walletChangeWs = null;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.connectWebSocket();
        this.connectWalletChangeWs();
        this.fetchStatsData();
        this.fetchChangesData();
        this.fetchServiceHealth();
        this._resizeListener = () => this.setState({ isMobile: isMobileViewport() });
        window.addEventListener('resize', this._resizeListener);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.searchParams !== this.props.searchParams) {
            const sp = this.props.searchParams;
            const nextStatus = sp.get('status') || 'suspended';
            const nextListed = sp.get('listed') !== 'false';
            const nextMarket = sp.get('market') || null;
            const nextCurr = sp.get('curr') || '';
            const nextRange = sp.get('range') || '7d';
            const nextKind = sp.get('kind') || 'all';
            if (
                nextStatus !== this.state.walletStatusFilter ||
                nextListed !== this.state.walletListedOnly ||
                nextMarket !== this.state.walletMarketFilter ||
                nextCurr !== this.state.walletChangesFilterCurrency ||
                nextRange !== this.state.walletChangesRange ||
                nextKind !== this.state.walletChangesKind
            ) {
                this.setState({
                    walletStatusFilter: nextStatus,
                    walletListedOnly: nextListed,
                    walletMarketFilter: nextMarket,
                    walletChangesFilterCurrency: nextCurr,
                    walletChangesRange: nextRange,
                    walletChangesKind: nextKind,
                    walletChangesOffset: 0,
                }, () => {
                    const changesChanged = (
                        nextCurr !== (prevProps.searchParams.get('curr') || '') ||
                        nextRange !== (prevProps.searchParams.get('range') || '7d') ||
                        nextKind !== (prevProps.searchParams.get('kind') || 'all')
                    );
                    if (changesChanged) this.fetchChangesData();
                });
            }
        }
    }

    componentWillUnmount() {
        this.stopWalletPolling();
        if (this.dashboardWs) {
            this.dashboardWs.disconnect();
            this.dashboardWs = null;
        }
        if (this.walletChangeWs) {
            this.walletChangeWs.disconnect();
            this.walletChangeWs = null;
        }
        if (this._resizeListener) window.removeEventListener('resize', this._resizeListener);
    }

    // URL 쿼리 write-through (V1 DEFAULTS 와 완전 일치 — 기본값은 삭제)
    syncUrl = (patch) => {
        if (!this.props.setSearchParams) return;
        const next = new URLSearchParams(this.props.searchParams);
        const DEFAULTS = {
            status: 'suspended',
            listed: 'true',
            market: null,
            curr: '',
            range: '7d',
            kind: 'all',
        };
        Object.entries(patch).forEach(([k, v]) => {
            const defaultVal = DEFAULTS[k];
            if (v === null || v === undefined || v === '' || String(v) === String(defaultVal)) {
                next.delete(k);
            } else {
                next.set(k, String(v));
            }
        });
        this.props.setSearchParams(next, { replace: true });
    };

    computeDateRange = (preset) => {
        const now = new Date();
        const endIso = now.toISOString();
        if (preset === 'today') {
            const start = new Date(now);
            start.setHours(0, 0, 0, 0);
            return [start.toISOString(), endIso];
        }
        if (preset === '7d') {
            const start = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        if (preset === '30d') {
            const start = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        return [null, null];
    };

    // ── WebSocket + 폴링 폴백 ──

    connectWebSocket = () => {
        const username = getStoredUsername();
        if (!username) { this.startWalletPolling(); return; }
        this.dashboardWs = new DashboardWebSocket(
            username, getAccessToken,
            this.handleWsMessage,
            this.handleWsStatusChange,
            this.handleWsAuthFailure
        );
        this.dashboardWs.connect();
    };

    connectWalletChangeWs = () => {
        this.walletChangeWs = new WalletChangeWebSocket(this.handleWalletChangeWsMessage);
        this.walletChangeWs.connect();
    };

    handleWalletChangeWsMessage = (message) => {
        if (!message || message.type !== 'wallet_change_update') return;
        this.fetchChangesData();
        this.fetchStatsData();
        this.fetchWalletStatusData();
    };

    handleWsMessage = (message) => {
        try {
            const { data } = message;
            if (!data) return;
            const { data_type, data: payload } = data;
            if (data_type === 'wallet_status_update') {
                const changes = payload.changes || payload;
                const changedIds = Array.isArray(changes)
                    ? changes.map(c => `${c.currency}-${c.net_type}`)
                    : [];
                this.setState({
                    walletStatus: Array.isArray(changes) ? changes : this.state.walletStatus,
                    walletStatusLastUpdated: new Date(),
                    walletHighlightIds: changedIds,
                });
                setTimeout(() => this.setState({ walletHighlightIds: [] }), 3000);
                this.fetchStatsData();
                this.fetchChangesData();
            }
        } catch (err) {
            console.error('[ExchangeNotiV2] WebSocket 메시지 처리 실패:', err);
        }
    };

    handleWsAuthFailure = () => {
        try { clearLoginSession(); } catch (e) {}
        window.location.replace('/login');
    };

    handleWsStatusChange = (status) => {
        if (status === 'connected') {
            this.stopWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: false });
            this.fetchWalletStatusData();
        } else if (status === 'disconnected') {
            this.startWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: true });
        } else {
            this.setState({ wsStatus: status });
        }
    };

    fetchWalletStatusData = () => {
        fetchWalletStatus({ exchange: 'upbit', listed_only: this.state.walletListedOnly })
            .then(res => this.setState({
                walletStatus: res.data || [],
                walletStatusLastUpdated: new Date(),
                walletStatusLoading: false,
                walletStatusError: false,
            }))
            .catch(err => {
                console.error('[ExchangeNotiV2] Failed to fetch wallet status:', err);
                showToast({ type: 'error', message: '지갑 상태 로드에 실패했습니다.' });
                this.setState({ walletStatusLoading: false, walletStatusError: true });
            });
    };

    startWalletPolling = () => {
        if (this.walletPollingInterval) return;
        this.fetchWalletStatusData();
        this.walletPollingInterval = setInterval(this.fetchWalletStatusData, 60000);
    };

    stopWalletPolling = () => {
        if (this.walletPollingInterval) {
            clearInterval(this.walletPollingInterval);
            this.walletPollingInterval = null;
        }
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onWalletSearchChange = (e) => {
        this.setState({ walletSearchQuery: e.target.value });
    };

    onWalletStatusFilterChange = (filter) => {
        this.setState({ walletStatusFilter: filter, walletChangesOffset: 0 });
        this.syncUrl({ status: filter });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletListedOnlyChange = (listedOnly) => {
        this.setState({ walletListedOnly: listedOnly, walletChangesOffset: 0 }, () => this.fetchWalletStatusData());
        this.syncUrl({ listed: String(listedOnly) });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletMarketFilterChange = (market) => {
        this.setState({ walletMarketFilter: market, walletChangesOffset: 0 });
        this.syncUrl({ market });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** DAS-40: 정지 카드 클릭 → suspended 필터 ON/OFF (toggle) */
    handleStatsCardClick = (filterKey) => {
        const isActive = this.state.walletStatusFilter === filterKey && this.state.walletMarketFilter === null;
        if (isActive) {
            this.setState({ walletStatusFilter: 'all', walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: 'all', market: null });
        } else {
            this.setState({ walletStatusFilter: filterKey, walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: filterKey, market: null });
        }
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ── 통계 / 변경 이력 / 서비스 상태 fetch ──

    fetchStatsData = () => {
        // 데이터가 없을 때(첫 로드)만 loading 표시 — WS/폴링 갱신마다 도넛 깜빡임 방지 (DAS-117)
        if (this.state.walletStats == null) this.setState({ walletStatsLoading: true });
        fetchWalletStatusStats()
            .then(res => this.setState({ walletStats: res.data, walletStatsLoading: false, walletStatsError: false }))
            .catch(err => {
                console.error('[ExchangeNotiV2] Failed to fetch wallet stats:', err);
                showToast({ type: 'error', message: '지갑 통계 로드에 실패했습니다.' });
                this.setState({ walletStatsLoading: false, walletStatsError: true });
            });
    };

    fetchChangesData = () => {
        this.setState({ walletChangesLoading: true });
        const params = { limit: 20, offset: this.state.walletChangesOffset };
        if (this.state.walletChangesFilterCurrency) {
            params.currency = this.state.walletChangesFilterCurrency;
        }
        const [dateFrom, dateTo] = this.computeDateRange(this.state.walletChangesRange);
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (this.state.walletChangesKind && this.state.walletChangesKind !== 'all') {
            params.kind = this.state.walletChangesKind;
        }
        fetchWalletStatusChanges(params)
            .then(res => this.setState({
                walletChanges: res.data?.results || [],
                walletChangesPagination: {
                    count: res.data?.count || 0,
                    next: res.data?.next,
                    previous: res.data?.previous,
                },
                walletChangesLoading: false,
                walletChangesError: false,
            }))
            .catch(err => {
                console.error('[ExchangeNotiV2] Failed to fetch wallet changes:', err);
                showToast({ type: 'error', message: '변경 이력 로드에 실패했습니다.' });
                this.setState({ walletChangesLoading: false, walletChangesError: true });
            });
    };

    fetchServiceHealth = () => {
        this.setState({ serviceHealthStatus: 'loading' });
        fetchNotiHealth()
            .then(res => {
                const st = (res.data && res.data.status === 'ok') ? 'ok' : 'error';
                this.setState({ serviceHealthStatus: st });
            })
            .catch(() => this.setState({ serviceHealthStatus: 'error' }));
    };

    onChangesPageChange = (direction) => {
        this.setState(prev => {
            const newOffset = direction === 'next'
                ? prev.walletChangesOffset + 20
                : Math.max(0, prev.walletChangesOffset - 20);
            return { walletChangesOffset: newOffset };
        }, () => this.fetchChangesData());
    };

    onChangesFilterCurrencyChange = (value) => {
        this.setState({ walletChangesFilterCurrency: value, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ curr: value });
    };

    onWalletChangesRangeChange = (range) => {
        this.setState({ walletChangesRange: range, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ range });
    };

    onWalletChangesKindChange = (kind) => {
        this.setState({ walletChangesKind: kind, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ kind });
    };

    render() {
        const {
            collapsed, isMobile,
            walletStats, walletStatsLoading, walletStatsError,
            walletStatus, walletStatusLoading, walletStatusLastUpdated, walletStatusError,
            walletSearchQuery, walletStatusFilter, walletListedOnly, walletMarketFilter,
            walletChanges, walletChangesLoading, walletChangesError, walletChangesPagination,
            walletChangesFilterCurrency, walletChangesRange, walletChangesKind,
            walletHighlightIds,
            wsStatus, isPollingFallback, serviceHealthStatus,
        } = this.state;

        const totalCoins = walletStats?.total_coins ?? '—';
        const normalCount = walletStats?.normal_count ?? '—';
        const suspendedCount = walletStats?.suspended_count ?? '—';
        const changes24h = walletStats?.recent_changes_24h ?? '—';
        const normalRatio = typeof walletStats?.normal_ratio === 'number'
            ? `${walletStats.normal_ratio.toFixed(1)}%`
            : '—';

        // ServiceConnectionIndicator 상태 결합
        let combinedServiceStatus = serviceHealthStatus;
        if (serviceHealthStatus === 'ok' && wsStatus === 'disconnected' && isPollingFallback) {
            combinedServiceStatus = 'degraded';
        } else if (serviceHealthStatus === 'error') {
            combinedServiceStatus = 'error';
        }

        const isLiveWs = wsStatus === 'connected' && !isPollingFallback;
        // DAS-40: 정지 카드 active 상태
        const suspendedActive = walletStatusFilter === 'suspended' && walletMarketFilter === null;

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />

                    {/* 시안 V1: .app-body { gap: 20 } — 카드 스택 */}
                    <div
                        className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5"
                        style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20 }}
                    >
                        {/* ── 페이지 헤더 (시안 V1 TopBar — breadcrumb + title + WS 상태) ── */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 8,
                        }}>
                            <div>
                                {/* breadcrumb: "거래소 / 지갑 상태" */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontSize: 11,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--color-content-muted)',
                                    fontFamily: 'JetBrains Mono, monospace',
                                    marginBottom: 4,
                                    flexWrap: 'wrap',
                                }}>
                                    <span>거래소</span>
                                    <span style={{ opacity: 0.4 }}>/</span>
                                    <span>지갑 상태</span>
                                    <ServiceConnectionIndicator status={combinedServiceStatus} />
                                </div>
                                {/* title + Live 인디케이터 */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <h1 style={{
                                        fontSize: isMobile ? 18 : 20,
                                        fontWeight: 700,
                                        color: 'var(--color-content)',
                                        letterSpacing: '-0.02em',
                                        margin: 0,
                                    }}>
                                        Wallet Status
                                    </h1>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <StatusDot tone={isLiveWs ? 'gain' : 'warning'} size={6} />
                                        <span style={{
                                            fontFamily: 'JetBrains Mono, monospace',
                                            fontSize: 11,
                                            color: 'var(--color-content-muted)',
                                        }}>
                                            {isLiveWs ? 'Live' : '폴링'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── 4-KPI row (시안 V1 .row gap:14 — 4-col, 모바일 2x2) ──
                         *   정지 코인 카드: outline border-loss + 클릭 진입 (DAS-40)
                         */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                    ? 'repeat(2, minmax(0, 1fr))'
                                    : 'repeat(4, minmax(0, 1fr))',
                                gap: 14,
                            }}
                            aria-label="지갑 통계 요약"
                        >
                            {walletStatsError ? (
                                <div style={{
                                    gridColumn: '1 / -1',
                                    padding: 16,
                                    borderRadius: 12,
                                    border: '1px solid var(--color-surface-border)',
                                    background: 'var(--color-loss-subtle)',
                                    color: 'var(--color-loss)',
                                    fontSize: 13,
                                    textAlign: 'center',
                                }}>
                                    데이터를 불러올 수 없습니다.
                                </div>
                            ) : walletStatsLoading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="skeleton"
                                        style={{
                                            height: isMobile ? 80 : 110,
                                            borderRadius: 16,
                                            background: 'var(--color-surface-card)',
                                            border: '1px solid var(--color-surface-border)',
                                        }}
                                    />
                                ))
                            ) : (
                                <>
                                    {/* 전체 코인 */}
                                    <KpiCell
                                        label="전체 코인"
                                        value={String(totalCoins)}
                                        isMobile={isMobile}
                                    />
                                    {/* 정상 비율 — gain 색 + sub "310 / 350" */}
                                    <KpiCell
                                        label="정상 비율"
                                        value={normalRatio}
                                        tone="gain"
                                        sub={`${normalCount} / ${totalCoins}`}
                                        isMobile={isMobile}
                                    />
                                    {/* 정지 코인 — outline border-loss + DAS-40 클릭 */}
                                    <KpiCell
                                        label="정지 코인"
                                        value={String(suspendedCount)}
                                        tone="loss"
                                        sub={typeof walletStats?.normal_ratio === 'number'
                                            ? `${(100 - walletStats.normal_ratio).toFixed(1)}% · 주의`
                                            : undefined}
                                        isMobile={isMobile}
                                        outline
                                        onClick={() => this.handleStatsCardClick('suspended')}
                                        active={suspendedActive}
                                    />
                                    {/* 24시간 변경 */}
                                    <KpiCell
                                        label="24시간 변경"
                                        value={String(changes24h)}
                                        sub={changes24h === 0 ? '변경 없음' : undefined}
                                        isMobile={isMobile}
                                    />
                                </>
                            )}
                        </div>

                        {/* ── 신규 상장 공지 (시안 V1 .c) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <SectionHead title="신규 상장 공지" />
                            <NewListingNoticesPanel />
                        </div>

                        {/* ── 거래지원 종료 공지 (시안 V1 .c) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <SectionHead title="거래지원 종료 공지" />
                            <DelistingNoticesPanel />
                        </div>

                        {/* ── 지갑 입출금 상태 (시안 V1 .c .c-head + 7-col 테이블) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <SectionHead title="지갑 입출금 상태" />
                            <WalletStatusTable
                                data={walletStatus}
                                loading={walletStatusLoading}
                                lastUpdated={walletStatusLastUpdated}
                                searchQuery={walletSearchQuery}
                                statusFilter={walletStatusFilter}
                                onSearchChange={this.onWalletSearchChange}
                                onStatusFilterChange={this.onWalletStatusFilterChange}
                                highlightIds={walletHighlightIds}
                                error={walletStatusError}
                                listedOnly={walletListedOnly}
                                onListedOnlyChange={this.onWalletListedOnlyChange}
                                marketFilter={walletMarketFilter}
                                onMarketFilterChange={this.onWalletMarketFilterChange}
                            />
                        </div>

                        {/* ── 상태 변경 이력 (시안 V1 .c — kind/curr/페이지네이션) ── */}
                        <div
                            style={{
                                borderRadius: 16,
                                background: 'var(--color-surface-card)',
                                border: '1px solid var(--color-surface-border)',
                                padding: 18,
                            }}
                        >
                            <SectionHead title="상태 변경 이력" />
                            <WalletChangeLog
                                changes={walletChanges}
                                loading={walletChangesLoading}
                                pagination={walletChangesPagination}
                                onPageChange={this.onChangesPageChange}
                                filterCurrency={walletChangesFilterCurrency}
                                onFilterCurrencyChange={this.onChangesFilterCurrencyChange}
                                error={walletChangesError}
                                range={walletChangesRange}
                                onRangeChange={this.onWalletChangesRangeChange}
                                kind={walletChangesKind}
                                onKindChange={this.onWalletChangesKindChange}
                            />
                        </div>
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default withRouter(ExchangeNotiPageV2);
